import { Component, inject, signal } from '@angular/core';
import { CrmInjectionToken, ObjectType } from '../../tokens-and-interfaces';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { catchError, combineLatest, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { CrmAIApiService } from '@vendasta/crm-integrations';
import { CRMTrackingService } from '../../shared-services';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { AsyncPipe } from '@angular/common';
import { AlertComponent } from '@vendasta/galaxy/alert/src/alert.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';

export interface CrmAISummaryModalData {
  objectId: string;
  objectType: ObjectType;
  objectName: string;
}

@Component({
  // requires selector to prevent ID collision
  selector: 'crm-ai-summary-modal',
  templateUrl: './ai-summary-modal.component.html',
  styleUrls: ['./ai-summary-modal.component.scss'],
  imports: [
    TranslateModule,
    GalaxyLoadingSpinnerModule,
    AsyncPipe,
    AlertComponent,
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatIcon,
  ],
})
export class CrmAISummaryModalComponent {
  public data = inject(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<CrmAISummaryModalComponent>);
  private readonly config = inject(CrmInjectionToken);
  private readonly crmAIService = inject(CrmAIApiService);
  private readonly trackingService = inject(CRMTrackingService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translateService = inject(TranslateService);
  private readonly aiIconService = inject(GalaxyAiIconService);

  readonly objectId: string;
  readonly objectType: ObjectType;
  readonly objectName: string;

  private readonly aiSummaryTrigger$$ = new Subject<void>();
  private readonly regenerateAISummary = signal<boolean>(false);
  protected readonly noAISummary = signal(false);
  protected readonly aiSummaryIsLoading = signal<boolean>(true);
  protected readonly aiSummaryResponse$: Observable<string>;

  constructor() {
    this.aiIconService.dummyInit();

    this.objectId = this.data.objectId;
    this.objectType = this.data.objectType;
    this.objectName = this.data.objectName;

    this.aiSummaryResponse$ = this.getAISummaryResponse();
  }

  protected regenerateSummary(regenerateSummary: boolean) {
    if (this.aiSummaryIsLoading()) {
      return;
    }
    this.regenerateAISummary.set(regenerateSummary);
    this.aiSummaryTrigger$$.next();
  }

  private getAISummaryResponse(): Observable<string> {
    return combineLatest([
      this.config.namespace$,
      this.config.locale$ ?? of(''),
      this.aiSummaryTrigger$$.pipe(startWith(undefined)),
    ]).pipe(
      tap(() => this.aiSummaryIsLoading.set(true)),
      switchMap(([namespace, locale]) => {
        const regenerateAISummary = this.regenerateAISummary();
        this.regenerateAISummary.set(false);
        if (!namespace || !this.objectType || !this.objectId) {
          return of('');
        }
        return this.crmAIService
          .getAiSummaryForCrmObject({
            crmObjectNamespace: namespace,
            crmObjectType: this.objectType,
            crmObjectId: this.objectId,
            language: locale,
            regenerateSummary: regenerateAISummary,
          })
          .pipe(
            map((response) => response?.response),
            tap((response) => {
              if (!response) {
                this.noAISummary.set(true);
              } else {
                this.noAISummary.set(false);
              }
              this.trackingService.trackEvent(this.objectType as ObjectType, 'loaded-crm-object-ai-quick-summary');
            }),
            map((response) => {
              if (this.noAISummary()) {
                return this.translateService.instant('AI_SUMMARY.NO_SUMMARY');
              }
              const parser = new DOMParser();
              return parser.parseFromString(response, 'text/html').body.innerHTML;
            }),
            catchError(() => {
              this.snackbarService.openErrorSnack('AI_SUMMARY.ERROR');
              this.trackingService.trackEvent(this.objectType as ObjectType, 'failed-load-crm-object-ai-quick-summary');
              return of('');
            }),
          );
      }),
      tap(() => this.aiSummaryIsLoading.set(false)),
    );
  }

  protected handleCancel(): void {
    this.dialogRef.close();
  }
}
